import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { type Truck } from './types';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const STATUS_COLORS: Record<string, string> = {
  active: '#4ade80',
  idle: '#fbbf24',
  down: '#f87171',
};

function formatSyncAge(ts: number, t: (k: string, o?: any) => any): string {
  if (!ts || ts <= 0) return '';
  const epochMs = ts < 1e12 ? ts * 1000 : ts;
  const diffSec = Math.floor((Date.now() - epochMs) / 1000);
  if (diffSec < 0 || diffSec > 86400 * 7) return '';
  if (diffSec < 5) return t('dm_truck_just_now');
  if (diffSec < 60) return t('dm_truck_sec_ago', { count: diffSec });
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return t('dm_truck_min_ago', { count: diffMin });
  return t('dm_truck_hr_ago', { count: Math.floor(diffMin / 60) });
}

function fuelColor(level: number | null): string {
  if (level === null) return '#6b7280';
  if (level < 20) return '#dc2626';
  if (level < 35) return '#f87171';
  return '#fbbf24';
}

interface TruckItemProps {
  truck: Truck;
  selected: boolean;
  onClick: () => void;
  onOverride: () => void;
  enableDarkTheme: boolean;
}

const TruckItem: React.FC<TruckItemProps> = ({
  truck,
  selected,
  onClick,
  onOverride,
  enableDarkTheme,
}) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const [flashing, setFlashing] = useState(false);
  const isFirst = useRef(true);
  useEffect(() => {
    if (isFirst.current) {
      isFirst.current = false;
      return;
    }
    setFlashing(true);
    const timer = setTimeout(() => {
      setFlashing(false);
    }, 700);
    return () => {
      clearTimeout(timer);
    };
  }, [truck.payload.current, truck.lastDumped, truck.trips.current]);

  const statusColor = STATUS_COLORS[truck.status];
  const boxBg = enableDarkTheme ? palette.boxBackground : palette.boxBackground;
  const borderCol = palette.cardBorderColor;
  const textPrimary = enableDarkTheme ? '#e2e8f0' : '#1a202c';
  const textMuted = enableDarkTheme ? '#6b7280' : '#8A92A0';
  const textAccent = enableDarkTheme ? '#a5b4fc' : palette.primary;
  const statValue = enableDarkTheme ? '#c9d8f0' : '#424242';

  return (
    <Box
      onClick={onClick}
      sx={{
        background: flashing
          ? 'rgba(99,102,241,0.2)'
          : selected
          ? 'rgba(99,102,241,0.12)'
          : boxBg,
        border: selected ? '1px solid #6366f1' : `1px solid ${borderCol}`,
        borderLeft: `3px solid ${statusColor}`,
        borderRadius: '6px',
        padding: '10px 12px',
        cursor: 'pointer',
        transition: flashing
          ? 'background 0.1s ease'
          : 'background 0.55s ease, border-color 0.18s ease',
        boxShadow: flashing ? '0 0 0 1px rgba(99,102,241,0.45)' : 'none',
        '&:hover': {
          borderColor: selected ? '#6366f1' : 'rgba(99,102,241,0.4)',
        },
      }}
    >
      {/* Header row */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '6px',
          mb: '5px',
        }}
      >
        {/* Left: bumperNumber + reason (from last:pubsub) */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            minWidth: 0,
            flexShrink: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: '11px',
              fontWeight: 700,
              color: textPrimary,
              whiteSpace: 'nowrap',
            }}
          >
            {truck.bumperNumber}
          </Typography>
          {truck.lastStatus?.reason && (
            <Box
              sx={{
                background: 'rgba(99,102,241,0.12)',
                color: '#a5b4fc',
                fontSize: '8px',
                fontWeight: 700,
                padding: '2px 5px',
                borderRadius: '3px',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              {truck.lastStatus.reason}
            </Box>
          )}
        </Box>
        {/* Right: status (from last:pubsub) + fuel */}
        <Box
          sx={{
            display: 'flex',
            gap: '5px',
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          {truck.lastStatus && (
            <Box
              sx={{
                background:
                  truck.lastStatus.status.toLowerCase() === 'down'
                    ? 'rgba(248,113,113,0.12)'
                    : 'rgba(74,222,128,0.12)',
                color:
                  truck.lastStatus.status.toLowerCase() === 'down'
                    ? '#f87171'
                    : '#4ade80',
                fontSize: '8px',
                fontWeight: 700,
                padding: '2px 5px',
                borderRadius: '3px',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                whiteSpace: 'nowrap',
              }}
            >
              {truck.lastStatus.status}
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '3px',
              background: 'rgba(253,230,138,0.1)',
              border: '0.5px solid rgba(251,191,36,0.2)',
              borderRadius: '3px',
              padding: '2px 5px',
            }}
          >
            <Typography
              sx={{
                fontSize: '9px',
                color: fuelColor(truck.fuelLevel),
                fontWeight: 600,
              }}
            >
              ⛽ {truck.fuelLevel !== null ? `${truck.fuelLevel}%` : '–'}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Operator + truckStatus */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          mb: '2px',
          minWidth: 0,
        }}
      >
        <Typography
          sx={{
            fontSize: '10px',
            color: textAccent,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            flexShrink: 1,
            minWidth: 0,
          }}
        >
          {truck.operator}
        </Typography>
      </Box>

      {/* Route */}
      {(truck.assignedLoadingPoint ?? truck.assignedDumpPoint) && (
        <Typography
          sx={{
            fontSize: '9px',
            color: textMuted,
            mb: '4px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {truck.assignedLoadingPoint ?? '–'} {t('x2192')}{' '}
          {truck.assignedDumpPoint ?? '–'}
        </Typography>
      )}

      {/* Stats grid — 2 rows × 2 cols */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          rowGap: '4px',
          columnGap: '8px',
          fontSize: '9px',
          color: textMuted,
          mb: '8px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '3px',
            minWidth: 0,
          }}
        >
          {t('dm_truck_trip')}:&nbsp;
          <span style={{ color: statValue, fontWeight: 600 }}>
            {truck.trips.current}/{truck.trips.target}
          </span>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '3px',
            minWidth: 0,
          }}
        >
          {t('dm_truck_payload')}:&nbsp;
          <span
            style={{
              color: statValue,
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {truck.payload.current.toLocaleString()}/
            {truck.payload.capacity.toLocaleString()}t
          </span>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '3px',
            minWidth: 0,
          }}
        >
          {t('dm_truck_eff')}:&nbsp;
          <span style={{ color: statValue, fontWeight: 600 }}>
            {truck.efficiency}t/hr
          </span>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '3px',
            minWidth: 0,
          }}
        >
          {t('dm_truck_last_dumped')}:&nbsp;
          <span style={{ color: '#4ade80', fontWeight: 600 }}>
            {truck.lastDumped != null && truck.lastDumped > 0
              ? `${truck.lastDumped.toLocaleString()}t`
              : '–'}
          </span>
        </Box>
      </Box>

      {/* Last sync */}
      {truck.lastSync != null && !!formatSyncAge(truck.lastSync, t) && (
        <Typography
          sx={{
            fontSize: '8px',
            color: textMuted,
            mb: '5px',
          }}
        >
          {t('dm_truck_synced')} {formatSyncAge(truck.lastSync, t)}
        </Typography>
      )}

      {/* Shift ended banner */}
      {truck.shiftEnded && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            background: 'rgba(251,191,36,0.1)',
            border: '1px solid rgba(251,191,36,0.3)',
            borderRadius: '4px',
            padding: '4px 8px',
            mb: '6px',
          }}
        >
          <Box
            sx={{
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              bgcolor: '#fbbf24',
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{ fontSize: '9px', fontWeight: 700, color: '#fbbf24' }}
          >
            {t('dm_shift_ended_by_driver')}
          </Typography>
        </Box>
      )}

      {/* Override Plan button */}
      <Box
        onClick={(e) => {
          e.stopPropagation();
          onOverride();
        }}
        sx={{
          bgcolor: 'primary.main',
          color: '#fff',
          borderRadius: '4px',
          padding: '5px 8px',
          textAlign: 'center',
          fontSize: '9px',
          fontWeight: 700,
          letterSpacing: '0.04em',
          cursor: 'pointer',
          transition: 'opacity 0.15s',
          '&:hover': { opacity: 0.85 },
        }}
      >
        ⚡ {t('dm_truck_override_plan')}
      </Box>
    </Box>
  );
};

interface Props {
  trucks: Truck[];
  totalHaulTrucks?: number;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onOverride: (id: string) => void;
  enableDarkTheme: boolean;
  noBorder?: boolean;
}

const TruckList: React.FC<Props> = ({
  trucks,
  totalHaulTrucks,
  selectedId,
  onSelect,
  onOverride,
  enableDarkTheme,
  noBorder = false,
}) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const active = trucks.filter((t) => t.status === 'active').length;
  const { t } = useTranslation();
  const panelBg = enableDarkTheme
    ? palette.boxBackground
    : palette.boxBackground;
  const borderCol = palette.cardBorderColor;
  const headerText = enableDarkTheme ? '#c9d8f0' : '#1a202c';

  return (
    <Box
      sx={{
        ...(noBorder
          ? { flex: 1 }
          : {
              width: '340px',
              minWidth: '300px',
              background: panelBg,
              border: `1px solid ${borderCol}`,
              borderRadius: '10px',
              flexShrink: 0,
            }),
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Panel header */}
      <Box
        sx={{
          padding: '12px 14px',
          borderBottom: `1px solid ${borderCol}`,
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            fontSize: '11px',
            fontWeight: 700,
            color: headerText,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
          }}
        >
          {t('dm_tab_trucks')} ({active}
          {totalHaulTrucks !== undefined ? ` / ${totalHaulTrucks}` : ''})
        </Typography>
      </Box>

      {/* Truck list */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          padding: '8px',
          display: 'flex',
          flexDirection: 'column',
          gap: '6px',
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-thumb': {
            background: borderCol,
            borderRadius: '2px',
          },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
        }}
      >
        {trucks.map((truck) => (
          <TruckItem
            key={truck.id}
            truck={truck}
            selected={selectedId === truck.id}
            enableDarkTheme={enableDarkTheme}
            onClick={() => {
              onSelect(truck.id);
            }}
            onOverride={() => {
              onOverride(truck.id);
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default TruckList;
